import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReceiptIcon, ArrowLeftIcon, DownloadSimpleIcon } from "@phosphor-icons/react";

// Mirror the backend fallback: invoices often omit quantity, so a $0 line with
// a unit price should show the price as its total, and the invoice total is the
// sum of those. Keeps older records (saved before the fix) displaying correctly.
const lineTotalOf = (it) => {
  const lt = Number(it?.line_total || 0);
  if (lt > 0) return lt;
  const qty = Number(it?.quantity || 0);
  const unit = Number(it?.unit_cost || 0);
  const calc = qty * unit;
  if (calc > 0) return calc;
  return unit > 0 ? unit : 0;
};
const purchaseTotalOf = (p) => {
  const t = Number(p?.total_cost || 0);
  if (t > 0) return t;
  return (p?.items || []).reduce((s, it) => s + lineTotalOf(it), 0);
};

/**
 * Slide-over panel showing recent parts-invoice purchases. Click a row to
 * see the full invoice image/PDF alongside the parsed line items.
 */
export default function PartsPurchasedPanel({ open, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parts-purchases?limit=100");
      setList(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { load(); setSelected(null); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl rounded-sm" data-testid="parts-purchased-panel">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            {selected && (
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900" data-testid="back-to-list">
                <ArrowLeftIcon size={16} weight="bold" />
              </button>
            )}
            <ReceiptIcon size={18} weight="fill" className="text-blue-600" />
            {selected ? `Invoice ${selected.invoice_number || "—"}` : "Recent Parts Purchases"}
          </DialogTitle>
        </DialogHeader>

        {!selected && (
          <div className="max-h-[600px] overflow-y-auto space-y-2" data-testid="purchases-list">
            {loading && <div className="text-center py-6 text-sm text-slate-500 font-mono">Loading…</div>}
            {!loading && list.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-500">
                No parts purchases yet. Use "Add via invoice" or "Add via text" on the Inventory page.
              </div>
            )}
            {list.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className="w-full text-left bg-white border border-slate-200 hover:border-blue-400 rounded-sm p-3 transition-colors"
                data-testid={`purchase-row-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{p.vendor_name || "Unknown vendor"}</div>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                      {p.invoice_date} · {p.invoice_number || "no invoice #"} · {p.items?.length || 0} item{p.items?.length === 1 ? "" : "s"}
                      {p.created_by_name && <span> · by {p.created_by_name}</span>}
                    </div>
                  </div>
                  <div className="font-display font-black text-lg text-emerald-700 tabular-nums">
                    ${purchaseTotalOf(p).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="grid md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto" data-testid="purchase-detail">
            <div className="space-y-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">/// Vendor</div>
                <div className="font-bold text-slate-900">{selected.vendor_name || "—"}</div>
                <div className="text-xs text-slate-500 font-mono">{selected.invoice_date} · Invoice #{selected.invoice_number || "—"}</div>
                <div className="text-xs text-slate-500 font-mono">Total: <span className="text-emerald-700 font-bold">${purchaseTotalOf(selected).toFixed(2)}</span></div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">/// Line items</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 font-mono">
                      <th className="text-left py-1">Part</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Unit</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.items || []).map((it, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5">
                          <div className="text-slate-900">{it.name || it.part_number || "—"}</div>
                          {it.part_number && <div className="font-mono text-[10px] text-slate-400">{it.part_number}</div>}
                        </td>
                        <td className="text-right font-mono tabular-nums">{Number(it.quantity) > 0 ? it.quantity : "—"}</td>
                        <td className="text-right font-mono tabular-nums">${Number(it.unit_cost || 0).toFixed(2)}</td>
                        <td className="text-right font-mono tabular-nums font-bold">${lineTotalOf(it).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center justify-between">
                <span>/// Invoice attachment</span>
                {selected.invoice_file_url && (
                  <a href={selected.invoice_file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11px] normal-case tracking-normal" data-testid="download-invoice">
                    <DownloadSimpleIcon size={12} weight="bold" className="inline mr-0.5" /> Open
                  </a>
                )}
              </div>
              {selected.invoice_file_url ? (
                selected.invoice_file_type?.startsWith("image/") ? (
                  <img src={selected.invoice_file_url} alt="Invoice" className="w-full rounded-sm border border-slate-200" />
                ) : (
                  <iframe src={selected.invoice_file_url} title="Invoice" className="w-full h-[520px] rounded-sm border border-slate-200" />
                )
              ) : (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-sm p-6 text-center">
                  No invoice file was attached with this purchase.
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
